/**
 * Redux slice for WebSocket state management
 * Manages real-time connection state and data updates
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { WebSocketStatus } from '../../services/websocketService';

// State shape for WebSocket
export interface WebSocketState {
  status: WebSocketStatus;
  isConnected: boolean;
  reconnectAttempts: number;
  lastError: string | null;
  lastConnectedAt: number | null;
  lastDisconnectedAt: number | null;
  subscribedTopics: string[];

  // Real-time data caches
  latestTrade: any | null;
  latestPosition: any | null;
  latestBreach: any | null;
  latestOrder: any | null;
  latestAlert: any | null;

  // Data arrays (limited to last N items)
  recentTrades: any[];
  recentPositions: any[];
  recentBreaches: any[];
  recentOrders: any[];
  recentAlerts: any[];
}

// Initial state
const initialState: WebSocketState = {
  status: WebSocketStatus.DISCONNECTED,
  isConnected: false,
  reconnectAttempts: 0,
  lastError: null,
  lastConnectedAt: null,
  lastDisconnectedAt: null,
  subscribedTopics: [],

  latestTrade: null,
  latestPosition: null,
  latestBreach: null,
  latestOrder: null,
  latestAlert: null,

  recentTrades: [],
  recentPositions: [],
  recentBreaches: [],
  recentOrders: [],
  recentAlerts: []
};

// Maximum items to keep in recent arrays
const MAX_RECENT_ITEMS = 50;

// Create the slice
const websocketSlice = createSlice({
  name: 'websocket',
  initialState,
  reducers: {
    // Connection status updates
    setConnectionStatus: (state, action: PayloadAction<WebSocketStatus>) => {
      state.status = action.payload;
      state.isConnected = action.payload === WebSocketStatus.CONNECTED;

      if (action.payload === WebSocketStatus.CONNECTED) {
        state.lastConnectedAt = Date.now();
        state.reconnectAttempts = 0;
        state.lastError = null;
      } else if (action.payload === WebSocketStatus.DISCONNECTED) {
        state.lastDisconnectedAt = Date.now();
      }
    },

    // Reconnection tracking
    incrementReconnectAttempt: (state) => {
      state.reconnectAttempts += 1;
    },

    resetReconnectAttempts: (state) => {
      state.reconnectAttempts = 0;
    },

    // Error handling
    setConnectionError: (state, action: PayloadAction<string>) => {
      state.lastError = action.payload;
      state.status = WebSocketStatus.ERROR;
    },

    clearConnectionError: (state) => {
      state.lastError = null;
    },

    // Topic subscription tracking
    addSubscribedTopic: (state, action: PayloadAction<string>) => {
      if (!state.subscribedTopics.includes(action.payload)) {
        state.subscribedTopics.push(action.payload);
      }
    },

    removeSubscribedTopic: (state, action: PayloadAction<string>) => {
      state.subscribedTopics = state.subscribedTopics.filter(
        topic => topic !== action.payload
      );
    },

    clearSubscribedTopics: (state) => {
      state.subscribedTopics = [];
    },

    // Trade updates
    receiveTrade: (state, action: PayloadAction<any>) => {
      state.latestTrade = action.payload;
      state.recentTrades = [
        action.payload,
        ...state.recentTrades.slice(0, MAX_RECENT_ITEMS - 1)
      ];
    },

    // Position updates
    receivePosition: (state, action: PayloadAction<any>) => {
      state.latestPosition = action.payload;

      // Update or add position in recent positions
      const existingIndex = state.recentPositions.findIndex(
        pos => pos.id === action.payload.id
      );

      if (existingIndex >= 0) {
        state.recentPositions[existingIndex] = action.payload;
      } else {
        state.recentPositions = [
          action.payload,
          ...state.recentPositions.slice(0, MAX_RECENT_ITEMS - 1)
        ];
      }
    },

    // Breach updates
    receiveBreach: (state, action: PayloadAction<any>) => {
      state.latestBreach = action.payload;
      state.recentBreaches = [
        action.payload,
        ...state.recentBreaches.slice(0, MAX_RECENT_ITEMS - 1)
      ];
    },

    // Order updates
    receiveOrder: (state, action: PayloadAction<any>) => {
      state.latestOrder = action.payload;

      // Update or add order in recent orders
      const existingIndex = state.recentOrders.findIndex(
        order => order.id === action.payload.id
      );

      if (existingIndex >= 0) {
        state.recentOrders[existingIndex] = action.payload;
      } else {
        state.recentOrders = [
          action.payload,
          ...state.recentOrders.slice(0, MAX_RECENT_ITEMS - 1)
        ];
      }
    },

    // Alert updates
    receiveAlert: (state, action: PayloadAction<any>) => {
      state.latestAlert = action.payload;
      state.recentAlerts = [
        action.payload,
        ...state.recentAlerts.slice(0, MAX_RECENT_ITEMS - 1)
      ];
    },

    // Clear data
    clearRecentTrades: (state) => {
      state.recentTrades = [];
      state.latestTrade = null;
    },

    clearRecentPositions: (state) => {
      state.recentPositions = [];
      state.latestPosition = null;
    },

    clearRecentBreaches: (state) => {
      state.recentBreaches = [];
      state.latestBreach = null;
    },

    clearRecentOrders: (state) => {
      state.recentOrders = [];
      state.latestOrder = null;
    },

    clearRecentAlerts: (state) => {
      state.recentAlerts = [];
      state.latestAlert = null;
    },

    clearAllData: (state) => {
      state.latestTrade = null;
      state.latestPosition = null;
      state.latestBreach = null;
      state.latestOrder = null;
      state.latestAlert = null;
      state.recentTrades = [];
      state.recentPositions = [];
      state.recentBreaches = [];
      state.recentOrders = [];
      state.recentAlerts = [];
    },

    // Reset entire state
    resetWebSocketState: () => initialState
  }
});

// Export actions
export const {
  setConnectionStatus,
  incrementReconnectAttempt,
  resetReconnectAttempts,
  setConnectionError,
  clearConnectionError,
  addSubscribedTopic,
  removeSubscribedTopic,
  clearSubscribedTopics,
  receiveTrade,
  receivePosition,
  receiveBreach,
  receiveOrder,
  receiveAlert,
  clearRecentTrades,
  clearRecentPositions,
  clearRecentBreaches,
  clearRecentOrders,
  clearRecentAlerts,
  clearAllData,
  resetWebSocketState
} = websocketSlice.actions;

// Export reducer
export default websocketSlice.reducer;